import type { RequestLeaveReviewType } from "views/RequestLeave/RequestLeaveReviewButtons"
import type { RequestStatisticDetailItem, RequestStatisticDetailSelection } from "views/RequestStatistics/types"

import { RequestLeaveAction, RequestLeaveStatus } from "__generated__/graphql"
import { useQuery } from "@apollo/client"
import {
  Alert,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import {
  DialogCloseButton,
  FlexColumn,
  getDataViewTableStyles,
  GridEmpty,
  useDataViewTableScroll,
} from "components/common"
import { DateTime } from "luxon"
import { REQUEST_ACTION_LABELS, REQUEST_STATUS_COLORS, REQUEST_STATUS_LABELS } from "views/RequestLeave/constants"
import RequestLeaveReviewButtons from "views/RequestLeave/RequestLeaveReviewButtons"
import { formatRequestLeavePeriod } from "views/RequestLeave/utils"
import { GET_USER_REQUEST_STATISTIC_DETAILS } from "views/RequestStatistics/graphql"
import { REQUEST_STATISTICS_LAYOUT } from "views/RequestStatistics/styles"

type Props = {
  endDate: string
  onClose: () => void
  onReview: (item: RequestStatisticDetailItem, type: RequestLeaveReviewType) => void
  open: boolean
  selection: null | RequestStatisticDetailSelection
  startDate: string
}

const formatAmount = (value: number) => new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 2 }).format(value)

const RequestStatisticDetailDialog = ({ endDate, onClose, onReview, open, selection, startDate }: Props) => {
  const { onBodyScroll, tableHeadRef } = useDataViewTableScroll()
  const { data, error, loading } = useQuery(GET_USER_REQUEST_STATISTIC_DETAILS, {
    fetchPolicy: "network-only",
    skip: !open || !selection,
    variables: {
      action: selection?.action ?? RequestLeaveAction.RequestLeave,
      fromDate: startDate,
      status: selection?.status,
      toDate: endDate,
      userId: selection?.userId ?? 0,
    },
  })
  const items = data?.getUserRequestStatisticDetails ?? []
  const unit = selection?.action === RequestLeaveAction.RequestLeave ? "ngày" : "lần"

  return (
    <Dialog maxWidth="lg" onClose={onClose} open={open}>
      <DialogTitle>
        <FlexColumn gap={0.5} pr={4}>
          <Typography variant="h6">
            {selection ? REQUEST_ACTION_LABELS[selection.action] : "Chi tiết request"} · {selection?.userName}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {DateTime.fromISO(startDate).toFormat("dd/MM/yyyy")} – {DateTime.fromISO(endDate).toFormat("dd/MM/yyyy")}
          </Typography>
        </FlexColumn>
        <DialogCloseButton aria-label="Đóng" onClick={onClose} />
      </DialogTitle>

      <DialogContent>
        <FlexColumn gap={2} minHeight={0}>
          {loading && <LinearProgress />}
          {error && <Alert severity="error">Không thể tải chi tiết request. Vui lòng thử lại.</Alert>}
          {!loading && !error && items.length === 0 && <GridEmpty />}
          {!error && items.length > 0 && (
            <TableContainer
              className="relative"
              component={Paper}
              sx={{ borderRadius: 2, height: REQUEST_STATISTICS_LAYOUT.detailTableHeight, overflow: "hidden" }}
              variant="outlined"
            >
              <Table
                sx={(theme) =>
                  getDataViewTableStyles(theme, {
                    gridTemplateColumns: REQUEST_STATISTICS_LAYOUT.detailTableGridColumns,
                    minWidth: REQUEST_STATISTICS_LAYOUT.detailTableMinWidth,
                  })
                }
              >
                <TableHead ref={tableHeadRef}>
                  <TableRow>
                    <TableCell align="center">ID</TableCell>
                    <TableCell align="center">Thời gian</TableCell>
                    <TableCell align="center">Trạng thái</TableCell>
                    <TableCell align="center">Lý do / ghi chú</TableCell>
                    <TableCell align="right">Khối lượng</TableCell>
                    <TableCell align="right">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody onScroll={onBodyScroll}>
                  {items.map((item) => (
                    <TableRow hover key={item.id}>
                      <TableCell align="center">#{item.id}</TableCell>
                      <TableCell align="center">{formatRequestLeavePeriod(item.startDate, item.endDate)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          color={REQUEST_STATUS_COLORS[item.status]}
                          label={REQUEST_STATUS_LABELS[item.status]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.reason}</Typography>
                        {item.note && (
                          <Typography color="text.secondary" variant="caption">
                            {item.note}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(item.amount)} {unit}
                      </TableCell>
                      <TableCell align="right">
                        <RequestLeaveReviewButtons
                          disabled={item.status !== RequestLeaveStatus.AwaitingApproval}
                          onReview={(type) => onReview(item, type)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </FlexColumn>
      </DialogContent>
    </Dialog>
  )
}

export default RequestStatisticDetailDialog
